import { UserRole } from '@front-app/services/UserApi/UserModel';
import { TRImage } from '@front-app/store/util/image';
import { Nullable } from '@front-app/util/types';
import { VisibilityOption } from '../GroupsApi/GroupEvent';
import { PreferenceToken } from '../GroupsApi/GroupModel';
import { ImageUrl } from '../ImageApi/ImageApi';
import {
	Address,
	AddressResponse,
	Base64String,
	EventType,
	HtmlString,
	PublicItem,
} from '../SharedModels';

export interface EventV2 {
	id: string;
	membershipRole: UserRole;
	name: string;
	termsAndConditions: string;
	categories: string[];
	eventTypes: string[];
	startDate: Date;
	endDate: Date;
	price: EventPrice;
	location: EventLocation;
	isPublished: boolean; // This can only be false for event admins
}

export interface EventAddressResponse extends AddressResponse {
	longitude: string;
	latitude: string;
	contactInfo: null | string;
	empty: boolean;
}

export interface FullEventResponse {
	id: string;
	name: string;
	type: string;
	description: HtmlString; // HTML
	role: UserRole;
	bannerLink: null | string;
	widgetHeaderHtml: string; // HTML
	widgetHtml: string; // HTML
	pageHtml: Base64String; // base64 HTML
	termsAndConditionsHtml: Base64String; // base64 HTML,
	lastVisited: Date | null;
	created: Date;
	categories: string[];
	preferenceTokens: PreferenceToken[];
	adminProperties: null;
	adminSettings: null;
	published: boolean; // This can only be false for event admins
	paymentRequired: boolean;
	termsRequired: boolean;
	eventSeriesPriceMax: null; // verify
	eventSeriesPriceCcy: null;
	eventSeriesFreeUserWpUrl: null;
	sponsorClientName: null;
	customHighlights: null;
	conditions: null;
	totalFileBytes: null;
	startDate: number;
	endDate: number;
	address: null | EventAddressResponse;
	allowRegistration: true;
	isInterested: false;
	venueImageSrc: string;
	paymentId: string;
	eventTypes: EventType[];
	participants: FullEventParticipant[];
	streams: FullEventStream[];
	onlineMeeting: FullEventMeetingButton;
	priceV2: EventPrice;
}

export interface FullEvent {
	id: string;
	name: string;
	type: string;
	description: HtmlString; // HTML
	role: UserRole;
	bannerLink: string;
	pageHtml: HtmlString; // HTML
	termsAndConditionsHtml: HtmlString; // HTML;
	lastVisited: Date | null;
	created: Date;
	categories: string[];
	preferenceTokens: PreferenceToken[];
	isPublished: boolean; // This can only be false for event admins
	paymentRequired: boolean;
	termsRequired: boolean;
	startDate: Date;
	endDate: Date;
	address: FullEventLocation;
	allowRegistration: boolean;
	isInterested: boolean;
	venueImageSrc: TRImage;
	paymentId: string;
	eventTypes: EventType[];
	participants: FullEventParticipant[];
	streams: FullEventStream[];
	onlineMeeting: FullEventMeetingButton;
	priceV2: EventPrice;
}

export interface FullEventLocation extends EventLocation {
	longitude: number;
	latitude: number;
	contactInfo: string;
}

export interface FullEventParticipant {
	timedRightId: string;
	name: string;
	participationRole: ParticipationRole;
	bio: string;
	avatarSrc: ImageUrl;
}

export interface FullEventStream {
	name: string;
	color: string; // HEX code (without the "#")
}

export enum ParticipationRole {
	SPEAKER = 'Speaker',
}

export enum EventMeetingVideoType {
	ZOOM = 'ZOOM',
	NONE = 'NONE',
	OTHER = 'OTHER',
}

export interface FullEventMeetingButton {
	videoType: EventMeetingVideoType;
	videoUrl: string;
	videoButtonText: string;
	videoButtonColor: string;
}

/**
 * A raw API result, without any mappings to JS objects, such as dates
 */
export interface EventV2Result {
	id: string;
	isPublished: boolean;
	membershipRole: UserRole;
	name: string;
	termsAndConditions: string;
	categories: string[];
	eventTypes: string[];
	startDate: string;
	endDate: string;
	price: EventPrice;
	location: EventLocation;
}

export interface EventLocation extends Address {
	empty: boolean;
}

export enum EventCurrency {
	CAD = 'CAD',
	USD = 'USD',
}

export interface EventPrice {
	/**
	 * @deprecated
	 */
	value?: string;
	numericValue: number;
	currency: EventCurrency;
	type: PriceOption;
}

export interface NonValidEventPrice {
	value?: string;
	numericValue: string;
	currency?: EventCurrency;
	type: PriceOption.VALUE;
}

export enum PriceOption {
	FREE = 'FREE',
	TBD = 'TBD',
	VALUE = 'VALUE',
	NONE = 'OFF',
}

export interface EventManagementData {
	id: string;
	groupIds: string[];
	name: string;
	purpose: HtmlString;
	startDate: string;
	endDate: string;

	// Admin config
	isPublished: boolean;
	displayAttendanceCounts: boolean;
	allowRegistration: boolean;
	visibility: VisibilityOption;

	// All the other information
	termsEnabled: boolean;
	requireTermsAcceptance: boolean;
	termsAndConditions: string;
	eventTypes: string[];
	categories: string[];
	location: Nullable<Address>;
	price: EventPrice;
	speakers: string[];
	streams: FullEventStream[];
	onlineMeeting: UpsertEventMeetingButton;
}

export type RawPublicEventData = PublicItem<EventV2Result>;
export type PublicEventData = PublicItem<EventV2>;

export interface UpsertEventMeetingButton {
	isEnabled: boolean;
	videoUrl: string;
	videoButtonText: string;
	videoButtonColor: string;
}

export enum EventImageType {
	LOGO = 'LOGO',
	BANNER = 'BANNER',
	VENUE = 'VENUE',
}

export enum EventNotificationType {
	INVITATION = 'invitation',
	REMINDER = 'reminder',
}

export enum ReminderTimeBeforeEvent {
	TEN_MINUTES = 'ten minutes',
	ONE_HOUR = 'one hour',
	ONE_DAY = 'one day',
	ONE_WEEK = 'one week',
}

export interface EventNotification {
	type: EventNotificationType,
	subject: string,
	topHtml: string,
	bottomHtml: string,
	lastModified: Date,
	metadata?: EventReminderMetadata,
}

export interface EventReminderMetadata {
	reminderTimeBeforeEvent: ReminderTimeBeforeEvent[],
	isReminderEventUrlEnabled: boolean,
}

export interface UpsertEventNotification {
	subject?: string,
	topHtml?: string,
	bottomHtml?: string,
	reminderTimeBeforeEvent?: ReminderTimeBeforeEvent[],
	isReminderEventUrlEnabled?: boolean,
}
